import { useMemo } from "react";
import { ServitutMatrix as ServitutMatrixView } from "src/components/ServitutRedegoerelseRapport";
import { ServitutFolderCompareModal } from "src/components/ServitutFolderCompareModal";
import { toViewPayload } from "../adapters/viewPayload";
import { selectAiJobIds, selectAktFetchProgress, selectIncompleteARows, selectRejectedARows, selectSavingIds } from "../store/selectors";
import { ServitutReportActionType } from "../store/actions";
import { useServitutReport } from "../store/context";
import type { useServitutReportEffects } from "../store/effects";
import type { ServitutRedegoerelsePhase } from "../model/report";

export function ServitutMatrix(props: {
   effects: ReturnType<typeof useServitutReportEffects>;
   canSeeA: boolean;
   canSeeB: boolean;
   canWriteA: boolean;
   canWriteB: boolean;
   onPhaseChange: (phase: ServitutRedegoerelsePhase) => void;
   onOpenRow: (rowId: string) => void;
   onOpenStedfaestelse: (rowId: string) => void;
   onApprovePages: (rowIds: string[]) => void;
   showFolderCompare: boolean;
   onShowFolderCompare: (open: boolean) => void;
}) {
   const { state, dispatch } = useServitutReport();
   const payload = useMemo(() => toViewPayload(state), [state]);
   const aiJobKeys = selectAiJobIds(state);
   const savingIds = selectSavingIds(state);
   const aktFetchProgress = selectAktFetchProgress(state);

   return (
      <>
         <ServitutMatrixView
            payload={payload as never}
            analyzing={aiJobKeys.size > 0}
            aiJobKeys={aiJobKeys}
            analysisProgress={props.effects.aiProgress}
            openingKey={state.openingRowId}
            exporting={state.exporting}
            exportProgress={state.exportProgress}
            savingAnnotationKeys={savingIds}
            justSavedKeys={new Set(state.justSavedIds)}
            unsavedChangeKeys={new Set(state.unsavedIds)}
            resolvingPaataleKeys={new Set(state.resolvingPaataleIds)}
            analysisField={state.analysisField}
            onAnalysisFieldChange={(next) => dispatch({ type: ServitutReportActionType.SetAnalysisField, payload: next })}
            onOpenRow={(row) => props.onOpenRow(row.key)}
            onAcceptField={(row, text) => { void props.effects.acceptField(row.key, state.analysisField, text); }}
            onUndoField={(row) => { void props.effects.revertField(row.key, state.analysisField); }}
            onResetField={(row) => dispatch({ type: ServitutReportActionType.ResetField, payload: { rowId: row.key, field: state.analysisField } })}
            onAcceptAllField={(entries) => {
               void props.effects.acceptAllField(
                  entries.map((entry) => ({ rowId: entry.row.key, text: entry.text })),
                  state.analysisField,
               );
            }}
            onUndoAllField={(rows) => { void props.effects.revertAllField(rows.map((row) => row.key), state.analysisField); }}
            onResolvePaataleberettiget={(row) => { void props.effects.resolvePaatale(row.key); }}
            onUploadCustomPdf={(row, file) => { void props.effects.uploadPdf(row.key, file); }}
            onSelectAkt={(row, aktId, source) => { void props.effects.selectAkt(row.key, aktId, source); }}
            onAnalyze={() => {
               void (state.phase === "B" ? props.effects.analyzeContentRows() : props.effects.locateRows());
            }}
            onStopAnalysis={() => props.effects.stopAnalysis(state.phase)}
            onStopAktFetch={() => props.effects.stopAktFetch()}
            onRunRowAi={(row) => {
               void (state.phase === "B" ? props.effects.analyzeContentRows([row.key]) : props.effects.locateRows([row.key]));
            }}
            canAnalyze={state.phase === "B" ? props.effects.hasContentRows() : !aktFetchProgress && props.effects.hasLocatableRows()}
            aktFetchProgress={aktFetchProgress}
            caseDescription={state.caseDescription}
            onCaseDescriptionChange={(value) => dispatch({ type: ServitutReportActionType.SetCaseDescription, payload: value })}
            onExport={(mode) => { void props.effects.exportTreated(mode); }}
            phase={state.phase}
            onPhaseChange={props.onPhaseChange}
            canSeeA={props.canSeeA}
            canSeeB={props.canSeeB}
            canWriteA={props.canWriteA}
            canWriteB={props.canWriteB}
            incompleteACount={selectIncompleteARows(state).length}
            rejectedACount={selectRejectedARows(state).length}
            onCompareFolder={() => props.onShowFolderCompare(true)}
            onOpenStedfaestelse={(row) => props.onOpenStedfaestelse(row.key)}
            onApprovePages={(row) => props.onApprovePages([row.key])}
            onRejectPages={(row) => { void props.effects.rejectPages([row.key]); }}
            onRevertPages={(row) => { void props.effects.revertPages([row.key]); }}
            onApproveAllPages={(rows) => props.onApprovePages(rows.map((row) => row.key))}
            onRevertAllPages={(rows) => { void props.effects.revertPages(rows.map((row) => row.key)); }}
         />
         {props.showFolderCompare ? (
            <ServitutFolderCompareModal
               rows={payload.matrix.rows}
               onClose={() => props.onShowFolderCompare(false)}
            />
         ) : null}
      </>
   );
}
